import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../../../router/all_routes";
import CollapseHeader from "../../../../../core/common/collapse-header";
import { checkPermission } from "../../../../../core/permissons/CheckPermissons";
import { Table, Button } from "react-bootstrap";
import apiDefinitions from "../../../../../core/api/apiDefinitions";
import {
  showSuccessAlert,
  showError,
} from "../../../../../core/alert/sweetAlertConfig";

const route = all_routes;

interface Approver {
  empNo: string;
  name: string;
  email: string;
  approverType: string;
  approverRole: string;
  status: string;
}

const DeactivateKeyApprover: React.FC = () => {
  const [approvers, setApprovers] = useState<Approver[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const navItems = [
    {
      label: "View Users",
      icon: "ti ti-layout-dashboard",
      link: route.ViewUsers,
      permission: "ROLE_GET_USERS",
    },
    {
      label: "Add Users",
      icon: "ti ti-plus",
      link: route.AddUsers,
      permission: "ROLE_ADD_USERS",
    },
    {
      label: "Add Key Approver",
      icon: "ti ti-key",
      link: route.addkeyapprover,
      permission: "ROLE_ASSIGN_APPROVER",
    },
    {
      label: "Deactivate Key Approver",
      icon: "ti ti-x",
      link: route.deactivatekeyapprover,
      permission: "ROLE_ASSIGN_APPROVER",
    },
  ];

  useEffect(() => {
    if (checkPermission("ROLE_ASSIGN_APPROVER")) {
      fetchApprovers();
    }
  }, []);

  const fetchApprovers = async () => {
    setLoading(true);
    try {
      const payload = {
        filterType: null,
        filterValue: "",
        date: null,
      };
      const response = await apiDefinitions.getAllApprovers(payload);
      if (response.data.status === "S1000") {
        const approversData: Approver[] = response.data.data.map((approver: any) => ({
          empNo: approver.empNo,
          name: approver.name || "Unknown",
          email: approver.email || "",
          approverType: approver.approverType,
          approverRole: approver.approverRole,
          status: approver.status,
        }));
        setApprovers(approversData);
      } else {
        showError("Failed to fetch approvers: " + response.data.statusDescription);
      }
    } catch (error: any) {
      showError(
        "Failed to fetch approvers: " + (error.response?.data?.statusDescription || "Network error")
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivateApprover = async (empNo: string, approverType: string) => {
    setLoading(true);
    try {
      const payload = { empNo, approverType };
      const response = await apiDefinitions.deactivateApprover(payload);
      if (response.data.status === "S1000") {
        showSuccessAlert("Approver deactivated successfully");
        fetchApprovers();
      } else {
        showError(`Failed to deactivate approver: ${response.data.statusDescription}`);
      }
    } catch (error: any) {
      showError(
        `Failed to deactivate approver: ${error.response?.data?.statusDescription || "Network error"}`
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              <div className="page-header">
                <div className="row align-items-center">
                  <div className="col-8">
                    <h4 className="page-title">Administration/Users/Deactivate Key Approver</h4>
                  </div>
                  <div className="col-4 text-end">
                    <div className="head-icons">
                      <CollapseHeader />
                    </div>
                  </div>
                </div>
              </div>
              <div className="card">
                <div className="card-body pb-0 pt-2">
                  <ul className="nav nav-tabs nav-tabs-bottom">
                    {navItems.map((item, index) =>
                      item.permission && checkPermission(item.permission) ? (
                        <li className="nav-item me-3" key={index}>
                          <Link
                            to={item.link}
                            className={`nav-link px-0 ${item.link === route.deactivatekeyapprover ? "active" : ""}`}
                          >
                            <i className={item.icon}></i> {item.label}
                          </Link>
                        </li>
                      ) : null
                    )}
                  </ul>
                </div>
              </div>
              <div className="row">
                <div className="col-md-12">
                  <div className="card">
                    <div className="card-body">
                      <h5 className="card-title">Current Approvers</h5>
                      <div className="table-responsive">
                        <Table className="table text-nowrap table-borderless">
                          <thead>
                            <tr>
                              <th scope="col">Name</th>
                              <th scope="col">Employee ID</th>
                              <th scope="col">Email</th>
                              <th scope="col">Approver Type</th>
                              <th scope="col">Status</th>
                              <th scope="col">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {loading ? (
                              <tr>
                                <td colSpan={6}>Loading...</td>
                              </tr>
                            ) : approvers.length === 0 ? (
                              <tr>
                                <td colSpan={6}>No approvers found</td>
                              </tr>
                            ) : (
                              approvers.map((approver) => (
                                <tr key={`${approver.empNo}-${approver.approverType}`}>
                                  <th scope="row">{approver.name}</th>
                                  <td>{approver.empNo}</td>
                                  <td>{approver.email}</td>
                                  <td>{approver.approverType}</td>
                                  <td>
                                    <span
                                      className={`badge bg-${
                                        approver.status === "ACTIVE" ? "success" : "secondary"
                                      }`}
                                    >
                                      {approver.status}
                                    </span>
                                  </td>
                                  <td className="text-center">
                                    {checkPermission("ROLE_ASSIGN_APPROVER") &&
                                      approver.status === "ACTIVE" && (
                                        <Button
                                          variant="danger"
                                          size="sm"
                                          onClick={() =>
                                            handleDeactivateApprover(approver.empNo, approver.approverType)
                                          }
                                          disabled={loading}
                                        >
                                          <i className="feather icon-x me-1"></i> Deactivate
                                        </Button>
                                      )}
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </Table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeactivateKeyApprover;